// Libraries
import { action, computed, observable } from "mobx";

// Core
import { ModelBase } from "Core/Models/ModelBase";

export class SuperAdminModel extends ModelBase<SuperAdminModel, SuperAdminModelDTO> {
    // #region Properties

    @observable
    public customers = observable<CustomerWithPortalAccessModel>([]);

    @observable
    public isLoading: boolean = false;

    @observable
    public error: string = "";

    // Filter states
    @observable
    public customerFilter: string = "ALL";

    @observable
    public plotFilter: string = "";

    @observable
    public houseTypeFilter: string = "ALL";

    @observable
    public developmentFilter: string = "ALL";

    // Statistics
    @observable
    public totalCustomers: number = 0;

    @observable
    public totalDevelopments: number = 0;

    @observable
    public totalHouseTypes: number = 0;

    @observable
    public filteredResults: number = 0;

    @observable
    public selectedCustomer: CustomerWithPortalAccessModel | null = null;

    // #endregion Properties

    @action
    public fromDto = (dto: SuperAdminModelDTO): void => {
        this.setCustomers(dto.customers || []);
        this.isLoading = dto.isLoading || false;
        this.error = dto.error;
        this.customerFilter = dto.customerFilter || "ALL";
        this.plotFilter = dto.plotFilter || "";
        this.houseTypeFilter = dto.houseTypeFilter || "ALL";
        this.developmentFilter = dto.developmentFilter || "ALL";
        this.totalCustomers = dto.totalCustomers || 0;
        this.totalDevelopments = dto.totalDevelopments || 0;
        this.totalHouseTypes = dto.totalHouseTypes || 0;
        this.filteredResults = dto.filteredResults || 0;
    };

    public toDto = (): SuperAdminModelDTO => {
        return {
            customers: this.customers.slice(),
            isLoading: this.isLoading,
            error: this.error,
            customerFilter: this.customerFilter,
            plotFilter: this.plotFilter,
            houseTypeFilter: this.houseTypeFilter,
            developmentFilter: this.developmentFilter,
            totalCustomers: this.totalCustomers,
            totalDevelopments: this.totalDevelopments,
            totalHouseTypes: this.totalHouseTypes,
            filteredResults: this.filteredResults,
        };
    };

    @action
    public setCustomerFilter = (value: string): void => {
        this.customerFilter = value;
        this.updateStatistics();
    };

    @action
    public setPlotFilter = (value: string): void => {
        this.plotFilter = value;
        this.updateStatistics();
    };

    @action
    public setHouseTypeFilter = (value: string): void => {
        this.houseTypeFilter = value;
        this.updateStatistics();
    };

    @action
    public setDevelopmentFilter = (value: string): void => {
        this.developmentFilter = value;
        this.updateStatistics();
    };

    @action
    public clearFilters = (): void => {
        this.customerFilter = "ALL";
        this.plotFilter = "";
        this.houseTypeFilter = "ALL";
        this.developmentFilter = "ALL";
        this.updateStatistics();
    };

    @action
    public setLoading = (loading: boolean): void => {
        this.isLoading = loading;
    };

    @action
    public setError = (error: string): void => {
        this.error = error;
    };

    @action
    public setCustomers = (customers: CustomerWithPortalAccessModelDTO[]): void => {
        this.customers.clear();
        customers.forEach(customerDto => {
            const customerModel = new CustomerWithPortalAccessModel();
            customerModel.fromDto(customerDto);
            this.customers.push(customerModel);
        });
        this.updateStatistics();
    };

    @action
    public setSelectedCustomer = (customer: CustomerWithPortalAccessModel): void => {
        this.selectedCustomer = customer;
    };

    @action
    private updateStatistics = (): void => {
        // Calculate filtered customers
        const filtered = this.getFilteredCustomers();
        this.filteredResults = filtered.length;

        // Update total statistics
        this.totalCustomers = this.customers.length;
        this.totalDevelopments = new Set(this.customers.map(c => c.developmentName)).size;
        this.totalHouseTypes = new Set(this.customers.map(c => c.houseTypeName)).size;
    };

    public getFilteredCustomers = (): CustomerWithPortalAccessModel[] => {
        return this.customers.filter(customer => {
            // If filter is empty or "ALL", match all items
            const customerMatch = this.customerFilter === "" || this.customerFilter === "ALL" || customer.customerName.toLowerCase().includes(this.customerFilter.toLowerCase());

            const plotMatch = this.plotFilter === "" || customer.plotNumber.includes(this.plotFilter);

            const houseTypeMatch =
                this.houseTypeFilter === "" || this.houseTypeFilter === "ALL" || customer.houseTypeName.toLowerCase().includes(this.houseTypeFilter.toLowerCase());

            const developmentMatch =
                this.developmentFilter === "" || this.developmentFilter === "ALL" || customer.developmentName.toLowerCase().includes(this.developmentFilter.toLowerCase());

            return customerMatch && plotMatch && houseTypeMatch && developmentMatch;
        });
    };

    // Group customers by customer ID and return structured data for table display
    public getGroupedCustomers = (): Array<{
        customer: CustomerWithPortalAccessModel;
        properties: CustomerWithPortalAccessModel[];
    }> => {
        const filteredCustomers = this.getFilteredCustomers();
        const groupedMap = new Map<string, CustomerWithPortalAccessModel[]>();

        // Group properties by customer ID
        filteredCustomers.forEach(customer => {
            const customerId = customer.customerId;
            if (!groupedMap.has(customerId)) {
                groupedMap.set(customerId, []);
            }
            groupedMap.get(customerId)!.push(customer);
        });

        // Convert to array format with customer info and properties
        return Array.from(groupedMap.entries()).map(([customerId, properties]) => ({
            customer: properties[0], // Use first property's customer info
            properties: properties,
        }));
    };

    public get canDisplayBusy(): boolean {
        return this.isLoading;
    }

    public get canDisplayError(): boolean {
        return this.error !== null;
    }

    public get canDisplayTable(): boolean {
        return !this.isLoading && !this.error;
    }
}

export class CustomerWithPortalAccessModel extends ModelBase<CustomerWithPortalAccessModel, CustomerWithPortalAccessModelDTO> {
    @observable
    public customerId: string = "";

    @observable
    public customerName: string = "";

    @observable
    public phoneNumber: string = "";

    @observable
    public emailAddress: string = "";

    @observable
    public plotNumber: string = "";

    @observable
    public houseTypeName: string = "";

    @observable
    public developmentName: string = "";

    @observable
    public addressLine1: string = "";

    @observable
    public addressLine2: string = "";

    @observable
    public city: string = "";

    @observable
    public postcode: string = "";

    @observable
    public propertyId: string = "";

    @computed
    public get developmentAddress(): string {
        const parts = [this.addressLine1 || "", this.addressLine2 || "", this.city || "", this.postcode || ""];
        return parts.filter(p => p && p.trim().length > 0).join(", ");
    }

    @action
    public fromDto = (dto: CustomerWithPortalAccessModelDTO): void => {
        this.customerId = dto.customerId || "";
        this.customerName = dto.customerName || "";
        this.phoneNumber = dto.phoneNumber || "";
        this.emailAddress = dto.emailAddress || "";
        this.plotNumber = dto.plotNumber || "";
        this.houseTypeName = dto.houseTypeName || "";
        this.developmentName = dto.developmentName || "";
        this.addressLine1 = dto.addressLine1 || "";
        this.addressLine2 = dto.addressLine2 || "";
        this.city = dto.city || "";
        this.postcode = dto.postcode || "";
        this.propertyId = dto.propertyId || "";
    };

    public toDto = (): CustomerWithPortalAccessModelDTO => {
        return {
            customerId: this.customerId,
            customerName: this.customerName,
            phoneNumber: this.phoneNumber,
            emailAddress: this.emailAddress,
            plotNumber: this.plotNumber,
            houseTypeName: this.houseTypeName,
            developmentName: this.developmentName,
            addressLine1: this.addressLine1,
            addressLine2: this.addressLine2,
            city: this.city,
            postcode: this.postcode,
            propertyId: this.propertyId,
        };
    };
}

export interface SuperAdminModelDTO {
    customers: CustomerWithPortalAccessModel[];
    isLoading: boolean;
    error: string;
    customerFilter?: string;
    plotFilter?: string;
    houseTypeFilter?: string;
    developmentFilter?: string;
    totalCustomers?: number;
    totalDevelopments?: number;
    totalHouseTypes?: number;
    filteredResults?: number;
}

export interface CustomerWithPortalAccessModelDTO {
    customerId: string;
    customerName: string;
    phoneNumber: string;
    emailAddress: string;
    plotNumber: string;
    houseTypeName: string;
    developmentName: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    postcode: string;
    propertyId: string;
}
